import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Loader from "../components/common/custom-component/Loader";
import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import CustomPagination from "../components/common/CustomPagination";
import DataContainer from "../components/common/DataContainer/DataContainer";

import { CalendarToday as CalendarIcon } from "@mui/icons-material";

import { Alert, Box, MenuItem, TextField, Typography, Divider } from "@mui/material";

import { useTranslation } from "react-i18next";
import { useToast } from "../components/common/custom-component/ToastProvider";

import { fetchPendingTasksRequest } from "../store/slices/workflows/pendingTasksSlice";
import { createPendingTasksRequest } from "../dto/workflowDto";
import SearchFilterBar from "../components/common/SearchFilterBar";
import { useSearchFilter } from "hooks/useSearchFilter";
import { useAuth } from "../hooks/useAuth";
import { useTableColumns } from "../hooks/useTableColumns";
import { claimTaskThunk } from "store/thunks/workflowThunks";
import { logger } from "helpers/logger";
import { toDDMMYYYY } from "helpers/dateUtils";

function UnassignedTaskPage() {
	const toast = useToast();
	const [t] = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const keycloakAuth = useAuth();
	const user = keycloakAuth.user;

	const hasDataInStore = useRef(false);

	const { data: UnassignedTaskData, loading, error } = useSelector((state) => ({ data: state.pendingTasks?.data?.data, loading: state.pendingTasks?.loading, error: state.pendingTasks?.error }));
	const selectedCompany = useSelector((state) => state.selectedCompany.data?.companyData);

	const [recordsPerPage, setRecordsPerPage] = useState(10);

	const [currentPage, setCurrentPage] = useState(() => {
		const savedPage = sessionStorage.getItem("unassignedTasksPageIndex");
		return savedPage ? parseInt(savedPage, 10) : 1;
	});

	const [sortField, setSortField] = useState("created_at");
	const [sortDirection, setSortDirection] = useState("desc");

	const [filters, setFilters] = useState({
		searchTerm: "",
		startDate: "",
		endDate: "",
	});

	const [searchParams, setSearchParams] = useState({
		searchTerm: "",
		startDate: "",
		endDate: "",
	});

	const [isLoadingBackdrop, setIsLoadingBackdrop] = useState(false);
	const [isBackgroundRefreshing, setIsBackgroundRefreshing] = useState(false);

	const totalCount = UnassignedTaskData?.totalCount;

	const totalPages = Math.max(1, Math.ceil((totalCount || 0) / recordsPerPage));

	// #region FILTERS & SORT

	const { setSearchQuery } = useSearchFilter(UnassignedTaskData?.tasks, ["generatedEmanifestID", "name", "taskName", "assignee", "taskId", "processInstanceId"], null);

	const applyFilters = () => {
		const newSearchParams = {
			searchTerm: filters.searchTerm?.trim() || "",
			startDate: toDDMMYYYY(filters.startDate),
			endDate: toDDMMYYYY(filters.endDate),
		};

		setSearchParams(newSearchParams);
		setSearchQuery(newSearchParams.searchTerm);
		setCurrentPage(1);

		if (!user?.id) return;

		dispatch(
			fetchPendingTasksRequest({
				request: createPendingTasksRequest({
					pageNumber: 1,
					recordsByPage: recordsPerPage,
					searchParam: newSearchParams.searchTerm,
					startDate: newSearchParams.startDate,
					endDate: newSearchParams.endDate,
					sortField,
					sortDirection,
				}),
			})
		);
	};

	const clearFilters = () => {
		const clearedUI = { searchTerm: "", startDate: "", endDate: "" };
		const clearedApplied = { searchTerm: "", startDate: "", endDate: "" };

		setFilters(clearedUI);
		setSearchParams(clearedApplied);
		setSearchQuery("");
		setCurrentPage(1);

		if (!user?.id) return;

		dispatch(
			fetchPendingTasksRequest({
				request: createPendingTasksRequest({
					pageNumber: 1,
					recordsByPage: recordsPerPage,
					sortField,
					sortDirection,
				}),
			})
		);
	};

	const handleSort = (column, direction) => {
		if (!column) return;

		let nextDirection;

		if (direction) {
			nextDirection = column === sortField && direction === sortDirection ? (sortDirection === "asc" ? "desc" : "asc") : direction;
		} else {
			nextDirection = column === sortField ? (sortDirection === "asc" ? "desc" : "asc") : "asc";
		}

		setSortField(column);
		setSortDirection(nextDirection);
		setCurrentPage(1);

		if (!user?.id) return;

		dispatch(
			fetchPendingTasksRequest({
				request: createPendingTasksRequest({
					pageNumber: 1,
					recordsByPage: recordsPerPage,
					searchParam: searchParams.searchTerm,
					startDate: searchParams.startDate,
					endDate: searchParams.endDate,
					sortField: column,
					sortDirection: nextDirection,
				}),
			})
		);
	};

	const handleChangeRecordsPerPage = (e) => {
		const value = Number(e.target.value) || 10;
		setRecordsPerPage(value);
		setCurrentPage(1);

		if (!user?.id) return;

		dispatch(
			fetchPendingTasksRequest({
				request: createPendingTasksRequest({
					pageNumber: 1,
					recordsByPage: value,
					searchParam: searchParams.searchTerm,
					startDate: searchParams.startDate,
					endDate: searchParams.endDate,
					sortField,
					sortDirection,
				}),
			})
		);
	};

	// #endregion


	// #region FETCH UNASSIGNEDTASKS

	useEffect(() => {
		if (!user?.id) return;

		dispatch(
			fetchPendingTasksRequest({
				request: createPendingTasksRequest({
					pageNumber: currentPage,
					recordsByPage: recordsPerPage,
					searchParam: searchParams.searchTerm,
					startDate: searchParams.startDate,
					endDate: searchParams.endDate,
					sortField,
					sortDirection,
				}),
			})
		);

		sessionStorage.removeItem("unassignedTasksPageIndex");
	}, [dispatch, user?.id, currentPage, recordsPerPage, searchParams, sortField, sortDirection, selectedCompany]);

	useEffect(() => {
		const hasExistingData = UnassignedTaskData?.tasks?.length > 0;

		if (!loading && isBackgroundRefreshing) {
			setIsBackgroundRefreshing(false);
		}

		if (hasExistingData) {
			hasDataInStore.current = hasExistingData;
		}

		if (loading && hasExistingData) {
			setIsBackgroundRefreshing(true);
		}
	}, [loading, isBackgroundRefreshing, UnassignedTaskData]);

	// #endregion

	// #region ASSIGN TASK

	const assignTask = useCallback(
		async (row) => {
			try {
				if (!user?.id) return;

				setIsLoadingBackdrop(true);
				const result = await dispatch(
					claimTaskThunk({
						taskId: row.taskId,
						userId: user.id,
					})
				);

				if (claimTaskThunk.fulfilled.match(result)) {
					sessionStorage.setItem("unassignedTasksPageIndex", String(currentPage));
					navigate(`/userTasks?generatedEmanifestId=${row.generatedEmanifestID}`);
				}
			} catch (error) {
				logger.error("assign Task failed:", error);
			} finally {
				setIsLoadingBackdrop(false);
			}
		},
		[user?.id, navigate, currentPage]
	);

	// #endregion

	// #region UNASSIGNEDTASKS COLUMNS

	const actions = useMemo(() => ({
		onAssign: assignTask,
	}), [assignTask]);

	const { columns, isLoading: columnsLoading } = useTableColumns('UnassignedTasks', { actions });

	// #endregion

	// #region RETURN

	if (loading && !hasDataInStore.current) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
				<Loader variant="dots" message="Loading..." />
			</Box>
		);
	}

	if (error && UnassignedTaskData?.tasks?.length === 0) {
		return <Alert severity="error">{error}</Alert>;
	}

	return (
		<Box sx={{ p: 3 }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
				<Typography variant="h3" color="primary">
					{t("Unassigned Tasks")}
				</Typography>

				{isBackgroundRefreshing && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Loader variant="circular" message="Loading..." size="small" />
					</Box>
				)}
			</Box>

			<Typography variant="body1" color="text.secondary" paragraph>
				{t("View and claim tasks that haven't been assigned to anyone yet.")}
			</Typography>

			<Divider sx={{ my: 3 }} />

			{/* Filters */}
			<Box
				sx={{
					my: 2,
					p: 2,
					borderRadius: 2,
					bgcolor: "rgba(29,131,84,0.04)",
					display: "flex",
					flexWrap: "wrap",
					alignItems: "center",
					gap: 2,
				}}
			>
				{/* Search */}
				<Box sx={{ flex: 1, minWidth: 260 }}>
					<SearchFilterBar
						/* Backend search & filtering */
						mode="backend"
						/* Search input */
						searchValue={filters.searchTerm}
						onSearchChange={(v) => setFilters((prev) => ({ ...prev, searchTerm: v }))}
						searchPlaceholder={t("Search Unassigned Task...")}
						searchBarProps={{
							sx: {
								bgcolor: "white",
								borderRadius: 1,
								"& .MuiOutlinedInput-root": { borderRadius: 1 },
							},
						}}
						/* Date range filters */
						showDateRange={true}
						startDateLabel={t("Start date")}
						endDateLabel={t("End date")}
						startDateValue={filters.startDate}
						endDateValue={filters.endDate}
						onStartDateChange={(v) => setFilters((prev) => ({ ...prev, startDate: v }))}
						onEndDateChange={(v) => setFilters((prev) => ({ ...prev, endDate: v }))}
						dateInputSx={{
							minWidth: 170,
							"& .MuiOutlinedInput-root": {
								"& fieldset": { borderColor: "primary.main" },
								"&:hover fieldset": { borderColor: "primary.main" },
								"&.Mui-focused fieldset": { borderColor: "primary.main" },
							},
						}}
						/* Apply / Clear actions */
						showActions={true}
						applyText={t("Apply")}
						clearText={t("Clear")}
						onApply={applyFilters}
						onClear={clearFilters}
						applyButtonProps={{
							sx: { display: "flex", alignItems: "center", gap: 1, textTransform: "uppercase" },
						}}
						clearButtonProps={{
							sx: { display: "flex", alignItems: "center", gap: 1, textTransform: "uppercase" },
						}}
						/* Disable unused UI elements */
						showFilterButton={false}
						showSearchButton={false}
					/>
				</Box>
			</Box>

			<Typography variant="body1">
				{t("Total Unassigned Tasks")}: {totalCount || 0}
			</Typography>

			{!UnassignedTaskData || UnassignedTaskData?.tasks?.length === 0 ? (
				<DataContainer data={[]} emptyStateIcon={CalendarIcon} emptyStateTitle={t("No Unassigned Tasks yet")} emptyStateSubtitle={t("Wait for someone to assign a task for your group")} />
			) : (
				<Box sx={{ mt: 2 }}>
					<DataContainer
						data={UnassignedTaskData?.tasks}
						columns={columns} columnsLoading={columnsLoading}
						onSort={handleSort}
						sortConfig={{ key: sortField, direction: sortDirection }}
						showRowNumbers
						maxHeight={600}
						stickyHeader
					/>
				</Box>
			)}

			<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
				<TextField
					select
					size="small"
					label={t("Per page")}
					value={recordsPerPage}
					onChange={handleChangeRecordsPerPage}
					sx={{
						minWidth: 120,
						"& .MuiOutlinedInput-root": {
							"& fieldset": { borderColor: "primary.main" },
							"&:hover fieldset": { borderColor: "primary.main" },
							"&.Mui-focused fieldset": { borderColor: "primary.main" },
						},
					}}
				>
					{[5, 10, 15].map((n) => (
						<MenuItem key={n} value={n}>
							{n}
						</MenuItem>
					))}
				</TextField>

				<CustomPagination
					currentPage={currentPage}
					totalPages={totalPages}
					onPageChange={(event, value) => setCurrentPage(value)}
					color="primary"
					showFirstButton
					showLastButton
					sx={{ mt: 2 }}
				/>
			</Box>

			<LoadingBackdrop open={isLoadingBackdrop} message={t("Loading data, please wait...")} variant="bars" color="greenGradient" />
		</Box>
	);

	// #endregion
}

export default UnassignedTaskPage;
